/**
 * Persian-inspired Loader Component
 * Beautiful animated loader with Persian calligraphy and geometric patterns
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { poemCacheService } from '../../services/poemCacheService';
import { useTheme } from '../../hooks/useTheme';

export interface PersianLoaderProps {
  size?: 'small' | 'medium' | 'large';
  variant?: 'default' | 'minimal' | 'ornate';
  message?: string;
  className?: string;
}

export const PersianLoader: React.FC<PersianLoaderProps> = ({
  size = 'medium',
  variant = 'default',
  message,
  className = ''
}) => {
  const sizeClasses = {
    small: 'w-4 h-4',
    medium: 'w-8 h-8',
    large: 'w-12 h-12'
  };

  const containerSizeClasses = {
    small: 'w-8 h-8',
    medium: 'w-16 h-16',
    large: 'w-24 h-24'
  };

  if (variant === 'minimal') {
    return (
      <div className={`flex flex-col items-center justify-center ${className}`}>
        <div className={`${sizeClasses[size]} relative`}>
          {/* Simple Persian letter rotation */}
          <div className="absolute inset-0 animate-spin">
            <div className="w-full h-full rounded-full border-4 border-purple-200 border-t-purple-600"></div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-purple-600 font-bold text-lg animate-pulse">پ</span>
          </div>
        </div>
        {message && (
          <p className="mt-2 text-sm text-gray-600 animate-pulse">{message}</p>
        )}
      </div>
    );
  }

  if (variant === 'ornate') {
    return (
      <div className={`flex flex-col items-center justify-center ${className}`}>
        <div className={`${containerSizeClasses[size]} relative`}>
          {/* Outer rotating ring with gradient */}
          <div className="absolute inset-0 animate-spin">
            <div className="w-full h-full rounded-full border-4 border-transparent bg-gradient-to-r from-purple-500 via-blue-500 to-pink-500 p-1">
              <div className="w-full h-full rounded-full bg-white dark:bg-gray-800"></div>
            </div>
          </div>

          {/* Multiple rotating rings with Persian patterns */}
          <div className="absolute inset-2 animate-spin-slow">
            <div className="w-full h-full rounded-full border-2 border-dashed border-purple-300/60"></div>
          </div>
          <div className="absolute inset-3 animate-spin-reverse">
            <div className="w-full h-full rounded-full border-2 border-dotted border-blue-300/60"></div>
          </div>

          {/* Ferdowsi statue inside the circle - centered and properly sized */}
          <div className="absolute inset-4 rounded-full overflow-hidden bg-gradient-to-br from-purple-50 to-pink-50">
            <div
              className="w-full h-full bg-contain bg-center bg-no-repeat opacity-40 filter grayscale"
              style={{
                backgroundImage: 'url(/images/ferdowsi-statue.jpg), url(/images/placeholder-ferdowsi.svg)',
                backgroundSize: '80%',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            ></div>

            {/* Overlay with Persian text */}
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-transparent via-white/20 to-transparent">
              <div className="text-center relative z-10">
                <div className="text-lg font-bold bg-gradient-to-r from-purple-600 via-blue-600 to-pink-600 bg-clip-text text-transparent animate-pulse drop-shadow-sm">
                  فارسی
                </div>
                <div className="text-xs text-gray-600 mt-1 font-persian animate-pulse">
                  فردوسی
                </div>
              </div>
            </div>
          </div>

          {/* Floating Persian letters around the circle */}
          <div className="absolute -top-2 -right-2 text-purple-500/80 text-sm animate-bounce font-persian drop-shadow-sm">ف</div>
          <div className="absolute -bottom-2 -left-2 text-blue-500/80 text-sm animate-bounce delay-150 font-persian drop-shadow-sm">ا</div>
          <div className="absolute -top-2 -left-2 text-pink-500/80 text-sm animate-bounce delay-300 font-persian drop-shadow-sm">ر</div>
          <div className="absolute -bottom-2 -right-2 text-green-500/80 text-sm animate-bounce delay-500 font-persian drop-shadow-sm">س</div>
        </div>
        {message && (
          <p className="mt-4 text-sm text-gray-700 animate-pulse text-center max-w-xs font-medium">{message}</p>
        )}
      </div>
    );
  }

  // Default variant
  return (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      <div className={`${containerSizeClasses[size]} relative`}>
        {/* Outer rotating ring with Persian geometric pattern */}
        <div className="absolute inset-0 animate-spin">
          <div className="w-full h-full rounded-full border-4 border-transparent bg-gradient-to-r from-purple-500 via-blue-500 to-pink-500 p-1">
            <div className="w-full h-full rounded-full bg-white"></div>
          </div>
        </div>

        {/* Inner counter-rotating ring */}
        <div className="absolute inset-2 animate-spin-reverse">
          <div className="w-full h-full rounded-full border-2 border-dashed border-purple-300"></div>
        </div>

        {/* Central Persian letter with pulsing effect */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative">
            {/* Glowing background */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full blur-lg opacity-30 animate-pulse"></div>

            {/* Main Persian letter */}
            <div className="relative text-3xl font-bold bg-gradient-to-r from-purple-600 via-blue-600 to-pink-600 bg-clip-text text-transparent animate-pulse">
              پ
            </div>
          </div>
        </div>

        {/* Orbiting dots */}
        <div className="absolute inset-0 animate-spin-slow">
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1 w-2 h-2 bg-purple-400 rounded-full"></div>
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1 w-2 h-2 bg-blue-400 rounded-full"></div>
          <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-pink-400 rounded-full"></div>
          <div className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-1 w-2 h-2 bg-green-400 rounded-full"></div>
        </div>
      </div>

      {message && (
        <p className="mt-4 text-sm text-gray-600 animate-pulse text-center max-w-xs">{message}</p>
      )}
    </div>
  );
};

// Full-screen overlay loader
export interface PersianOverlayLoaderProps {
  isVisible: boolean;
  message?: string;
  variant?: 'default' | 'minimal' | 'ornate';
  onCancel?: () => void;
}

export const PersianOverlayLoader: React.FC<PersianOverlayLoaderProps> = ({
  isVisible,
  message,
  variant = 'default',
  onCancel
}) => {
  const { t } = useTranslation('common');
  const { isDark } = useTheme();
  const displayMessage = message ?? t('loading');
  const [shouldRender, setShouldRender] = React.useState(isVisible);
  const [isAnimating, setIsAnimating] = React.useState(isVisible);
  const [randomPoem, setRandomPoem] = useState<{
    title: string;
    content: string;
    author: string;
    translation?: string;
  } | null>(null);

  // Note: variant parameter available for future enhancements
  console.log('PersianOverlayLoader variant:', variant);

  // Add a callback version of loadRandomPoem to avoid dependency issues
  const loadRandomPoem = React.useCallback(async () => {
    try {
      console.log('PersianLoader: Starting poem loading process...');

      // Force refresh cache to ensure we have the latest poems
      console.log('PersianLoader: Force refreshing cache to get latest poems...');
      const allPoems = await poemCacheService.refreshCache();
      console.log('PersianLoader: Force refresh returned poems:', allPoems.length);

      // Now try to get a random poem from the refreshed cache
      const cachedPoem = await poemCacheService.getRandomPoem();
      
      if (cachedPoem) {
        console.log('PersianLoader: SUCCESS - Using cached poem from database:', {
          id: cachedPoem.id,
          title: cachedPoem.title,
          author: cachedPoem.author,
          isActive: cachedPoem.isActive
        });
        setRandomPoem({
          title: cachedPoem.title,
          content: cachedPoem.content,
          author: cachedPoem.author,
          ...(cachedPoem.translation && { translation: cachedPoem.translation })
        });
        return;
      }

      // If no cached poems available after loading, use fallback poems
      console.log('PersianLoader: No cached poems available after force refresh, using fallback');
      const fallbackPoems = [
        {
          title: 'Shahnameh',
          content: 'به نام خداوند جان و خرد',
          author: 'Ferdowsi',
          translation: 'In the name of God, the Lord of life and wisdom'
        },
        {
          title: 'Divan-e Hafez',
          content: 'الا یا ایها الساقی ادر کاسا و ناولها',
          author: 'Hafez',
          translation: 'O cupbearer, bring forth the cup and put it to our lips'
        },
        {
          title: 'Rubaiyat',
          content: 'گر دست دهد پارهٔ نانی خوش باش',
          author: 'Omar Khayyam',
          translation: 'If you can get a piece of bread, be happy'
        },
        {
          title: 'Masnavi',
          content: 'بشنو از نی چون حکایت می‌کند',
          author: 'Rumi',
          translation: 'Listen to the reed flute, how it tells a tale'
        }
      ];

      const randomIndex = Math.floor(Math.random() * fallbackPoems.length);
      const randomFallback = fallbackPoems[randomIndex];
      if (randomFallback) {
        console.log('PersianLoader: Using fallback poem:', randomFallback.title);
        setRandomPoem(randomFallback);
      }
    } catch (error) {
      console.error('PersianLoader: Failed to load random poem:', error);
      
      // Always fallback to default poems on error
      const fallbackPoems = [
        {
          title: 'Shahnameh',
          content: 'به نام خداوند جان و خرد',
          author: 'Ferdowsi',
          translation: 'In the name of God, the Lord of life and wisdom'
        },
        {
          title: 'Divan-e Hafez',
          content: 'الا یا ایها الساقی ادر کاسا و ناولها',
          author: 'Hafez',
          translation: 'O cupbearer, bring forth the cup and put it to our lips'
        },
        {
          title: 'Rubaiyat',
          content: 'گر دست دهد پارهٔ نانی خوش باش',
          author: 'Omar Khayyam',
          translation: 'If you can get a piece of bread, be happy'
        },
        {
          title: 'Masnavi',
          content: 'بشنو از نی چون حکایت می‌کند',
          author: 'Rumi',
          translation: 'Listen to the reed flute, how it tells a tale'
        }
      ];

      const randomIndex = Math.floor(Math.random() * fallbackPoems.length);
      const randomFallback = fallbackPoems[randomIndex];
      if (randomFallback) {
        console.log('PersianLoader: Using fallback poem after error:', randomFallback.title);
        setRandomPoem(randomFallback);
      }
    }
  }, []);

  // Load random poem when loader becomes visible or component mounts
  useEffect(() => {
    if (isVisible) {
      console.log('PersianLoader: Loader became visible, loading random poem...');
      loadRandomPoem();
    }
  }, [isVisible, loadRandomPoem]);

  // Load poem on component mount
  useEffect(() => {
    console.log('PersianLoader: Component mounted, attempting to load poem...');
    loadRandomPoem();
  }, [loadRandomPoem]);

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      // Small delay to trigger animation
      const animationTimeout = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(animationTimeout);
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before unmounting
      const unmountTimeout = setTimeout(() => setShouldRender(false), 200);
      return () => clearTimeout(unmountTimeout);
    }
  }, [isVisible]);

  if (!shouldRender) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
      style={{ pointerEvents: isVisible ? 'auto' : 'none' }}
    >
      {/* Enhanced background with Ferdowsi statue watermark - 60% transparency */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900/60 via-blue-900/60 to-purple-900/60">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: 'url(/images/ferdowsi-statue.jpg), url(/images/placeholder-ferdowsi.svg)',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'grayscale(80%) contrast(1.1) brightness(0.8)'
          }}
        ></div>
        <div className="absolute inset-0 backdrop-blur-sm"></div>
      </div>

      {/* Loading container with Persian-inspired design - 30% transparency */}
      <div
        className={`relative backdrop-blur-md rounded-3xl p-6 shadow-2xl border mx-4 transform transition-all duration-300 ${isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
          }`}
        style={{
          background: isDark 
            ? 'linear-gradient(135deg, rgba(31,41,55,0.3) 0%, rgba(55,65,81,0.3) 100%)' 
            : 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(248,250,252,0.3) 100%)',
          borderColor: isDark ? 'rgba(55, 65, 81, 0.2)' : 'rgba(255, 255, 255, 0.2)',
          boxShadow: isDark
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(55, 65, 81, 0.2)'
            : '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.2)',
          width: 'fit-content',
          minWidth: '280px'
        }}
      >
        {/* Subtle Persian pattern overlay */}
        <div className="absolute inset-0 rounded-3xl opacity-5">
          <div className="w-full h-full bg-gradient-to-br from-purple-500 via-blue-500 to-pink-500 rounded-3xl"></div>
        </div>

        <div className="relative z-10">
          {/* Elegant dual spinning circles */}
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 relative">
              {/* Outer circle - clockwise */}
              <div className="absolute inset-0 animate-spin">
                <div 
                  className="w-full h-full rounded-full border-2"
                  style={{
                    borderColor: isDark ? '#374151' : '#e5e7eb',
                    borderTopColor: isDark ? '#a78bfa' : '#c084fc',
                    borderRightColor: isDark ? '#c4b5fd' : '#d8b4fe'
                  }}
                ></div>
              </div>

              {/* Inner circle - counter-clockwise */}
              <div className="absolute inset-2 animate-spin-reverse">
                <div 
                  className="w-full h-full rounded-full border-2"
                  style={{
                    borderColor: isDark ? '#4b5563' : '#f3f4f6',
                    borderBottomColor: isDark ? '#60a5fa' : '#60a5fa',
                    borderLeftColor: isDark ? '#93c5fd' : '#93c5fd'
                  }}
                ></div>
              </div>

              {/* Center dot */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full animate-pulse"></div>
              </div>
            </div>
          </div>

          {/* Action message at top */}
          {displayMessage && (
            <div className="text-center mb-4">
              <div 
                className="text-sm font-medium"
                style={{ color: isDark ? '#d1d5db' : '#374151' }}
              >
                {displayMessage}
              </div>
            </div>
          )}

          {/* Random poem from admin-managed poems */}
          <div className="text-center">
            {randomPoem ? (
              <>
                {/* Persian poem content — light text in dark mode for contrast on overlay */}
                <div className="text-xs text-gray-800 dark:text-gray-300 font-persian leading-relaxed font-medium">
                  {randomPoem.content.length > 80
                    ? randomPoem.content.substring(0, 80) + '...'
                    : randomPoem.content}
                </div>
                {/* English translation */}
                {randomPoem.translation && (
                  <div className="text-xs text-gray-500 dark:text-gray-500 mt-2 italic">
                    "{randomPoem.translation.length > 100
                      ? randomPoem.translation.substring(0, 100) + '...'
                      : randomPoem.translation}"
                  </div>
                )}
                {/* Author */}
                <div className="text-xs text-gray-500 dark:text-gray-600 mt-1">
                  — {randomPoem.author}
                </div>
              </>
            ) : (
              <>
                <div className="text-xs text-gray-800 dark:text-gray-300 font-persian leading-relaxed font-medium">
                  به نام خداوند جان و خرد
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-500 mt-2 italic">
                  "In the name of God, the Lord of life and wisdom"
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-600 mt-1">
                  — Ferdowsi
                </div>
              </>
            )}
          </div>

          {onCancel && (
            <button
              onClick={onCancel}
              className="w-full mt-4 px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 transition-all duration-200 rounded-lg hover:bg-gray-100/80 dark:hover:bg-gray-700/50 border border-gray-200/50 dark:border-gray-600/50"
            >
              {t('cancel')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersianLoader;